import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import R from 'ramda';
import Link from '@cotyorg/ccx-atoms/Link';
import Heading from '@cotyorg/ccx-atoms/Heading';
import BBCodeParser from '@cotyorg/ccx-utils/js/components/BBCodeParser';
import Button from '@cotyorg/ccx-atoms/Button';

const HeroContentWrapper = ({ heroWrapperContentClassName, children }) => (
  heroWrapperContentClassName ? (
    <div className={heroWrapperContentClassName}>{children}</div>
  ) : (
    children
  )
);

export const generateCtaCssClass = ({ ctaType, ctaExtraClass }) => classNames(
  'hero__cta-link',
  {
    'button': ctaType === 'button',
    [`${ctaExtraClass}__${ctaType}`]: ctaType && ctaExtraClass,
  },
);

export const generateCtaButtonCssClass = ({ ctaExtraClass }) => classNames(
  'hero__cta-button',
  'button',
  {
    [`${ctaExtraClass}_button`]: ctaExtraClass,
  },
);

export const generateHeroLabelCssClass = ({ labelExtraClass }) => classNames(
  'hero__label',
  {
    [`${labelExtraClass}`]: labelExtraClass,
  },
);

export const generateHeroHeadingCssClass = ({ headingExtraClass }) => classNames(
  'hero__heading',
  {
    [`${headingExtraClass}`]: headingExtraClass,
  },
);

export const generateHeroHeadingIntroCssClass = ({ headingIntroExtraClass }) => classNames(
  'hero__heading-introduction',
  {
    [`${headingIntroExtraClass}`]: headingIntroExtraClass,
  },
);

export const generateHeroDescriptionCssClass = ({ descriptionExtraClass }) => classNames(
  'hero__text',
  {
    [`${descriptionExtraClass}`]: descriptionExtraClass,
  },
);

HeroContentWrapper.propTypes = {
  heroWrapperContentClassName: PropTypes.string,
  children: PropTypes.node,
};

const HeroHeading = ({ headingHasLink, links, children }) => (
  headingHasLink && Array.isArray(links) ? (
    <Link extraClass="hero__heading-link" link={R.path([0, 'url'], links)}>{children}</Link>
  ) : (
    children
  )
);

HeroHeading.propTypes = {
  headingHasLink: PropTypes.bool,
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
  })),
  children: PropTypes.node,
};

const HeroContent = ({
  heading,
  headingIntroduction,
  label,
  description,
  links,
  ctaType,
  isUnderlined,
  heroContentClassName,
  heroContentBoxClassName,
  heroWrapperContentClassName,
  headingLevel,
  labelExtraClass,
  headingExtraClass,
  headingIntroExtraClass,
  descriptionExtraClass,
  ctaExtraClass,
  onClick,
  headingHasLink,
  openLinkInNewTab,
}) => (
  <HeroContentWrapper heroWrapperContentClassName={heroWrapperContentClassName}>
    <div className={heroContentClassName}>
      <div className={heroContentBoxClassName}>
        {label && (
          <div className={generateHeroLabelCssClass({ labelExtraClass })}>
            {label}
          </div>
        )}
        {(heading || headingIntroduction) && (
          <Heading
            data-test="hero-content-heading"
            isUnderlined={isUnderlined}
            level={headingLevel}
            extraClass={generateHeroHeadingCssClass({ headingExtraClass })}
          >
            {headingIntroduction && (
              <span className={generateHeroHeadingIntroCssClass({ headingIntroExtraClass })}>
                {headingIntroduction}
              </span>
            )}
            <HeroHeading
              links={links}
              headingHasLink={headingHasLink}
            >
              {heading}
            </HeroHeading>
          </Heading>
        )}

        {description && (
          R.compose(
            R.addIndex(R.map)((paragraph, index) => (
              <p
                data-test="hero-content-description"
                key={index}
                className={generateHeroDescriptionCssClass({ descriptionExtraClass })}
              >
                <BBCodeParser>
                  {paragraph}
                </BBCodeParser>
              </p>
            )),
            R.filter(paragraph => !!paragraph),
            R.split(/\n/),
          )(description)
        )}

        {Array.isArray(links) && links.map((link, index) => (
          onClick ? (
            <Button
              key={index} // eslint-disable-line
              extraClass={generateCtaButtonCssClass({ ctaExtraClass })}
              onClick={() => onClick({ ctaIndex: index })}
            >
              {R.prop('text', link)}
            </Button>
          ) : (
            <Link
              key={index} // eslint-disable-line
              extraClass={generateCtaCssClass({ ctaExtraClass, ctaType })}
              link={R.prop('url', link)}
              {...(openLinkInNewTab || R.prop('openInNewWindow', link)) && { target: '_blank' }}
            >
              {R.prop('text', link)}
            </Link>
          )
        ))}
      </div>
    </div>
  </HeroContentWrapper>
);

HeroContent.propTypes = {
  heading: PropTypes.string,
  headingIntroduction: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
    openInNewWindow: PropTypes.bool,
  })),
  isUnderlined: PropTypes.bool,
  heroContentClassName: PropTypes.string,
  heroContentBoxClassName: PropTypes.string,
  heroWrapperContentClassName: PropTypes.string,
  labelExtraClass: PropTypes.string,
  headingExtraClass: PropTypes.string,
  headingIntroExtraClass: PropTypes.string,
  descriptionExtraClass: PropTypes.string,
  ctaExtraClass: PropTypes.string,
  ctaType: PropTypes.oneOf(['link', 'button']),
  headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  onClick: PropTypes.func,
  headingHasLink: PropTypes.bool,
  openLinkInNewTab: PropTypes.bool,
};

export default HeroContent;
